"use client";

import { ChevronLeft } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useLocaleLegacy from "@/hooks/use-locale-legacy";
import locale from "@/locales/comparison.json";
import { Link, useRouter } from "@/src/i18n/navigation";
import { useLocale } from "next-intl";

interface Props {
  types: {
    id: string;
    text: string;
    map: string;
    mini_map: string;
    unit: string;
    area: string;
    facility: { id: string; label: string; icon: string }[];
  }[];
}
export default function Client(props: Props) {
  const { t } = useLocaleLegacy(locale);
  const lang = useLocale();

  const qs = useSearchParams();

  const [firstType, setFirstType] = useState(props.types[0]);
  const [secondType, setsecondType] = useState(props.types[1]);

  const [, firstTypeGross] = firstType.area.split("/");
  const [, secondTypeGross] = secondType.area.split("/");

  useEffect(() => {
    setFirstType(props.types[0]);
    setsecondType(props.types[1]);
  }, [props.types]);

  return (
    <div className="bg-coffebrown">
      <div className="grid grid-cols-2 px-10 xl:px-32 gap-x-2 xl:gap-x-20">
        <div>
          <Select
            value={firstType.id}
            onValueChange={(val) => {
              setFirstType(props.types.find((type) => type.id === val));
            }}
          >
            <SelectTrigger
              useIcon
              className="w-full rounded-[15px] xl:px-3 xl:py-2 px-2 py-1 xl:rounded-[3.6rem] bg-gold border-none cursor-pointer [&_[data-slot='select-value']]:w-full [&_[data-slot='select-value']]:flex [&_[data-slot='select-value']]:justify-center"
            >
              <SelectValue
                placeholder={
                  <span className="text-white font-medium font-pluto text-[8px] xl:text-xl">
                    {t("select_unit_type")}
                  </span>
                }
                className="text-white font-medium placeholder:text-white text-[8px] xl:text-xl"
              />
            </SelectTrigger>
            <SelectContent className="bg-darkgold border-0 px-4 w-[var(--radix-select-trigger-width)]">
              {props.types
                .filter((type) => type.id !== secondType.id)
                .map((type) => (
                  <SelectItem
                    key={type.id}
                    value={type.id}
                    className="rounded-none border-b border-b-white [&:last-child]:border-b-0 cursor-pointer py-2"
                  >
                    <div className="text-white text-sm xl:text-xl font-medium">
                      {type.text} - {type.unit}
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <img
            src={firstType.map}
            alt=""
            className="h-[86px] xl:h-72 w-full object-contain my-8"
          />

          <hr className="border-[.5px] border-gold w-full mb-6" />

          <div className="flex flex-col xl:flex-row">
            <div className="flex-1">
              <p className="text-white text-[8px] xl:text-2xl">
                {firstType.unit}
              </p>
              <p className="text-white xl:text-4xl">{firstTypeGross} sqm</p>
              <p className="text-white text-[8px] xl:text-2xl font-medium">
                Semi Gross
              </p>
            </div>
            <div className="flex-1 flex flex-col gap-y-1.5 xl:gap-y-3.5">
              {firstType.facility.map((facility) => (
                <div key={facility.id} className="inline-flex items-center">
                  <Image
                    src={facility.icon}
                    alt=""
                    width={32}
                    height={32}
                    className="w-5 xl:w-8 h-5 xl:h-8"
                  />
                  <p className="text-white font-medium ml-2 text-[10px] xl:text-2xl">
                    {facility.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <Select
            value={secondType.id}
            onValueChange={(val) => {
              setsecondType(props.types.find((type) => type.id === val));
            }}
          >
            <SelectTrigger
              useIcon
              className="w-full rounded-[15px] xl:px-3 xl:py-2 px-2 py-1 xl:rounded-[3.6rem] bg-gold border-none cursor-pointer [&_[data-slot='select-value']]:w-full [&_[data-slot='select-value']]:flex [&_[data-slot='select-value']]:justify-center"
            >
              <SelectValue
                placeholder={
                  <span className="text-white font-medium font-pluto text-[8px] xl:text-xl">
                    {t("select_unit_type")}
                  </span>
                }
                className="text-white font-medium placeholder:text-white text-[8px] xl:text-xl"
              />
            </SelectTrigger>
            <SelectContent className="bg-darkgold border-0 px-4 w-[var(--radix-select-trigger-width)]">
              {props.types
                .filter((type) => type.id !== firstType.id)
                .map((type) => (
                  <SelectItem
                    key={type.id}
                    value={type.id}
                    className="rounded-none border-b border-b-white [&:last-child]:border-b-0 cursor-pointer py-2"
                  >
                    <span className="text-white text-sm xl:text-xl font-medium">
                      {type.text} - {type.unit}
                    </span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <img
            src={secondType.map}
            alt=""
            className="h-[86px] xl:h-72 w-full object-contain my-8"
          />

          <hr className="border-[.5px] border-gold w-full mb-6" />

          <div className="flex flex-col xl:flex-row">
            <div className="flex-1">
              <p className="text-white text-[8px] xl:text-2xl">
                {secondType.unit}
              </p>
              <p className="text-white xl:text-4xl">{secondTypeGross} sqm</p>
              <p className="text-white text-[8px] xl:text-2xl font-medium">
                Semi Gross
              </p>
            </div>
            <div className="flex-1 flex flex-col gap-y-1.5 xl:gap-y-3.5">
              {secondType.facility.map((facility) => (
                <div key={facility.id} className="inline-flex items-center">
                  <Image
                    src={facility.icon}
                    alt=""
                    width={32}
                    height={32}
                    className="w-5 xl:w-8 h-5 xl:h-8"
                  />
                  <p className="text-white font-medium ml-2 text-[10px] xl:text-2xl">
                    {facility.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full inline-flex justify-center mt-12 xl:mt-14 my-14 xl:mb-20">
        <Link
          href={{
            pathname: "/harmony-tower",
            query: new URLSearchParams(qs).toString(),
          }}
          locale={lang}
        >
          <button className="text-white border border-gold hover:bg-gold hover:text-white rounded-[3.6rem] inline-flex items-center py-1 xl:py-3 px-3.5 xl:px-8 cursor-pointer">
            <ChevronLeft size={24} className="w-3.5 xl:w-6 h-3.5 xl:h-6" />
            <span className="ml-1 text-[10px] xl:text-xl font-medium">
              {t("back_to")}
            </span>
          </button>
        </Link>
      </div>
    </div>
  );
}
